import { createSelector, Selector } from '@ngrx/store';
import { Map } from 'immutable';
import { VehicleType } from 'src/graphql/generated/graphql.generated';
import { selectAllVehicleSummaryEntities } from 'src/store/generated/vehicle-summary-record.generated';
import { AppState } from '../appstate';
import { selectControlZoneEntitiesByCompanyId } from '../generated/control-zone-record.generated';
import { selectAllVehicleControlZoneEntities } from '../generated/vehicle-control-zone-record.generated';
import { selectAllVehicleEntities, VehicleRecord } from '../generated/vehicle-record.generated';
import { selectAllVehicles } from './entity-store-selectors';
import { selectOperationById } from './operation-selectors';
import { selectOperationByCardNumber } from './shared-selectors';
import { selectTripByTripId } from './trip-selectors';

export function selectVehicleById(
  vehicleId: string,
): Selector<AppState, VehicleRecord | undefined> {
  return createSelector(selectAllVehicles, (v) => v.get(vehicleId));
}

export function selectVehicleSummaryEntityById(vehicleId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (vehicles) =>
    vehicles.find((v) => v.id === vehicleId),
  );
}

export function selectVehiclesByControlZoneCompanyId(companyId: string) {
  return createSelector(
    selectControlZoneEntitiesByCompanyId(companyId),
    selectAllVehicleControlZoneEntities,
    selectAllVehicleEntities,
    (controlZones, vehicleControlZones, vehicles) => {
      const controlZoneIds = controlZones.map((c) => c.id);
      vehicleControlZones = vehicleControlZones.filter(
        (v) => v.controlZoneId != null && controlZoneIds.contains(v.controlZoneId),
      );
      const vehicleControlZoneVehicleIds = vehicleControlZones.map((c) => c.vehicleId);
      const cz = controlZones.first(undefined);
      return cz != null
        ? vehicles.filter((v) => vehicleControlZoneVehicleIds.contains(v.id))
        : Map();
    },
  );
}

export function selectVehiclesByCompany(companyId: string) {
  return createSelector(selectAllVehicles, (vehicles) =>
    vehicles.valueSeq().filter((v) => v.companyId === companyId),
  );
}

export function selectVehiclesByCompanyOrDispatchCompany(companyId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (vehicles) =>
    vehicles
      .valueSeq()
      .filter((v) => v.companyId === companyId || v.dispatchCompanyId === companyId),
  );
}

export function selectGroundVehiclesByCompany(companyId: string) {
  return createSelector(selectAllVehicles, (vehicles) =>
    vehicles
      .valueSeq()
      .filter((v) => v.companyId === companyId)
      .filter((v) => v.type === VehicleType.Ground),
  );
}

export function selectPrimaryVehicleByOperation(operationId: string) {
  return createSelector(
    selectAllVehicles,
    selectOperationById(operationId),
    (vehicles, operation) => {
      return operation != null
        ? vehicles.valueSeq().find((v) => v.id === operation.vehicleId)
        : undefined;
    },
  );
}

export function selectPrimaryVehicleByCardNumber(cardNumber: string) {
  return createSelector(
    selectOperationByCardNumber(cardNumber),
    selectAllVehicles,
    (operation, vehicles) =>
      operation == null
        ? undefined
        : vehicles.find((vehicle) => vehicle.id === operation.vehicleId),
  );
}

export function selectPrimaryVehicleByCardId(id: string) {
  return createSelector(selectOperationByCardNumber(id), selectAllVehicles, (operation, vehicles) =>
    operation == null ? undefined : vehicles.find((vehicle) => vehicle.id === operation.vehicleId),
  );
}

export function selectVehicleByTripId(tripId: string) {
  return createSelector(selectTripByTripId(tripId), selectAllVehicles, (trip, vehicles) => {
    return trip != null && trip.vehicleId != null ? vehicles.get(trip.vehicleId) : undefined;
  });
}
